/**
 * Shared fetch helpers for the platform-admin pages (DataBridge, Monitoring).
 * All admin endpoints wrap payloads in the `{ data }` / `{ data, meta }`
 * envelopes and are called with the bearer + org headers from useApiHeaders.
 */

export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** 503 from the old-system-backed DataBridge endpoints (connectivity/config). */
export class ConnectionError extends Error {}

export interface PaginationMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

async function fetchEnvelope<E>(url: string, headers: HeadersInit): Promise<E> {
  const res = await fetch(url, { headers });
  if (res.status === 503) {
    const body = (await res.json().catch(() => null)) as { detail?: string } | null;
    throw new ConnectionError(
      body?.detail ?? "Old system is unavailable. Check backend configuration and connectivity.",
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as E;
}

/** Unwrap a `{ data: T }` single-item response. */
export async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  return (await fetchEnvelope<{ data: T }>(url, headers)).data;
}

/** A `{ data: T[]; meta }` paginated list response, both halves returned. */
export async function fetchList<T>(
  url: string,
  headers: HeadersInit,
): Promise<{ data: T[]; meta: PaginationMeta }> {
  return fetchEnvelope<{ data: T[]; meta: PaginationMeta }>(url, headers);
}

/**
 * The human half of a failed admin fetch (POS2-1138 CP3).
 *
 * `fetchEnvelope` throws `Error("<status>: <raw body>")`, and for a FastAPI 4xx that body is
 * `{"detail": "..."}` — which is a sentence written for a person and then buried in JSON. The two
 * new platform-admin screens validate ranges server-side and rely on that sentence reaching the
 * reader, so this digs it back out.
 *
 * Falls back to the message as thrown. A parse failure here must not replace a real error with a
 * worse one.
 */
export function errorDetail(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const body = message.slice(message.indexOf(":") + 1).trim();
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail !== "") return parsed.detail;
  } catch {
    // Not JSON — a 503 from ConnectionError, a network failure, or a plain-text body.
  }
  return message;
}
